import { runInAction } from "mobx";

import { searchSuggestions } from "@/components/suggestions/utils";
import { groupSelection } from "@/input/utils/groupUngroup";
import { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import { GroupNodeModel } from "@/models/nodes/GroupNodeModel";
import {
  closingSymbols,
  closingToOpeningMap,
  openingSymbols,
  openingToClosingMap,
} from "@/models/nodes/groups/utils";
import { MatrixNodeModel } from "@/models/nodes/MatrixNodeModel";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";

import {
  closeOpenGroupLeft,
  closeOpenGroupRight,
  shouldCloseOpenGroupLeft,
  shouldCloseOpenGroupRight,
  shouldApplyGroupSymbols,
  applyGroupSymbols,
} from "../utils/openGroup";

import type { KeyboardInputAction } from "@/input/types";
import type { KeyCombination, Symbol } from "@corca/data";

const groupShortcuts: Partial<Record<KeyCombination | Symbol, KeyboardInputAction>> =
  {} satisfies Partial<Record<KeyCombination | Symbol, KeyboardInputAction>>;

for (const closingSymbol of closingSymbols) {
  groupShortcuts[closingSymbol] = {
    canPickSuggestion: (doc, input) => {
      return searchSuggestions(`${input}${closingSymbol}`, doc, []).length === 0;
    },
    action: (doc) => {
      if (doc.selection.hasSelectedNodes) {
        const openingSymbol = closingToOpeningMap[closingSymbol];
        doc.transact(() => {
          groupSelection(doc, openingSymbol, closingSymbol);
        }, `shortcut:group:wrap-selected`);
        return true;
      }

      let parent = doc.cursor.node?.parent;
      let endOfNode = false;

      if (parent?.is(MatrixNodeModel)) {
        endOfNode =
          !doc.cursor.nodeAfterCursor &&
          parent.getCellPosition(doc.cursor.node)?.col === parent.cols - 1;
        parent = parent.parent;
      } else if (parent?.is(FunctionNodeModel)) {
        endOfNode = doc.cursor.index === doc.cursor.node?.nodes.length;
      }

      if ((parent?.is(GroupNodeModel) || parent?.is(FunctionNodeModel)) && endOfNode) {
        if (parent.closingSymbol === closingSymbol) {
          if (parent.is(GroupNodeModel) && parent.props.omitParentheses) {
            doc.transact(() => {
              parent.omitParentheses = false;
            }, `shortcut:group:close`);
          }

          if (parent.is(FunctionNodeModel)) {
            doc.transact(() => {
              parent.parenthesesVisibility = "visible";
            }, `shortcut:group:close`);
          }

          doc.cursor.moveAfterNode(parent);
          return true;
        }
      }

      if (searchSuggestions(closingSymbol, doc, []).length > 1) {
        return false;
      }

      if (shouldCloseOpenGroupRight(doc)) {
        doc.transact(() => {
          doc.editor.resetInput();
          runInAction(() => {
            closeOpenGroupRight(doc, closingSymbol);
          });
        }, `shortcut:group:close`);
        return true;
      }

      if (parent?.is(VariableNodeModel)) {
        doc.transact(() => {
          parent.closingSymbol = closingSymbol;
          doc.cursor.moveAfterNode(parent);
        }, `shortcut:group:close`);
        return true;
      }

      return false;
    },
  };
}

for (const openingSymbol of openingSymbols) {
  groupShortcuts[openingSymbol] = {
    canPickSuggestion: (doc, input) => {
      return searchSuggestions(`${input}${openingSymbol}`, doc, []).length === 0;
    },
    action: (doc) => {
      if (doc.selection.hasSelectedNodes) {
        const closingSymbol = openingToClosingMap[openingSymbol];
        doc.transact(() => {
          groupSelection(doc, openingSymbol, closingSymbol);
        }, `shortcut:group:wrap-selected`);
        return true;
      }

      if (searchSuggestions(openingSymbol, doc, []).length > 1) {
        return false;
      }

      if (shouldCloseOpenGroupLeft(doc)) {
        doc.transact(() => {
          doc.editor.resetInput();
          closeOpenGroupLeft(doc, openingSymbol);
        }, `shortcut:group:open`);
        return true;
      }

      const functionNode = shouldApplyGroupSymbols(doc);

      if (functionNode) {
        doc.transact(() => {
          applyGroupSymbols(functionNode, openingSymbol, openingToClosingMap[openingSymbol]);
        }, `shortcut:group:apply-symbols`);
        return true;
      }

      return false;
    },
  };
}

export { groupShortcuts };
